import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, Button, Alert } from 'antd';
import axios from 'axios';
import { Encrypt } from '../../utils/encryption';
import { resetPassword } from '../../config/api';
import {
  PrefixFormItem,
  mobileForApi,
  isMobileLengthValid,
  MOBILE_MAX_LEN,
  mobileFieldInputPlain,
  userNameDisplayFromPrefix,
} from '../../constants/CountryCodes';
import { AUTH } from '../../config/zinihubConfig';
import { setResetStep1Data, resetResetPasswordData } from '../../store/resetPasswordSlice';
import styles from './ResetPassword.module.css';

function OtpInput({ value, onChange, disabled, error, setError }) {
  const inputRefs = useRef([]);
  const digits = (value || '').split('').concat(Array(6).fill('')).slice(0, 6);

  const handleChange = (idx, e) => {
    const char = e.target.value;
    if (!char) return;
    const newVal = (value || '').split('');
    newVal[idx] = char.slice(0, 1);
    onChange(newVal.join('').slice(0, 6));
    if (idx < 5) {
      const next = inputRefs.current[idx + 1];
      if (next) setTimeout(() => next.focus(), 10);
    }
    if (setError) setError(null);
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      const newVal = (value || '').split('');
      if (newVal[idx]) {
        newVal[idx] = '';
        onChange(newVal.join(''));
      } else if (idx > 0) {
        newVal[idx - 1] = '';
        onChange(newVal.join(''));
        inputRefs.current[idx - 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = (e.clipboardData?.getData('text') || '').slice(0, 6);
    onChange(paste);
    const lastIdx = Math.min(paste.length, 5);
    setTimeout(() => inputRefs.current[lastIdx]?.focus(), 10);
    if (setError) setError(null);
  };

  return (
    <div className={styles.otpDigitBox}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          className={styles.otpDigit}
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
}

function ResetPassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const resetPasswordFormData = useSelector((s) => s.resetPassword);

  const [step, setStep] = useState(1);

  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [showCodeInput, setShowCodeInput] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [mobileOtp, setMobileOtp] = useState('');
  const [verificationToken, setVerificationToken] = useState(null);
  const [confirmOtpLoading, setConfirmOtpLoading] = useState(false);
  const [mobileOtpVerified, setMobileOtpVerified] = useState(false);

  const [apiError, setApiError] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const mobileInputRef = useRef(null);

  const [form] = Form.useForm();
  const mobileWatch = Form.useWatch('mobile', form);

  useEffect(() => {
    dispatch(resetResetPasswordData());
  }, [dispatch]);

  const fmtCountdown = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setTimeout(() => { setResendCountdown((c) => c - 1); }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const resetOtp = () => {
    setMobileOtp('');
    setShowCodeInput(false);
    setVerificationToken(null);
    setIsMobileVerified(false);
    setMobileOtpVerified(false);
    setOtpError(null);
    dispatch(setResetStep1Data({ prefix: resetPasswordFormData.prefix, mobile: resetPasswordFormData.mobile, verifiedMobileToken: null }));
    form.setFieldsValue({ userName: '' });
  };

  const handleVerifyClick = async (action = '') => {
    const prefix = form.getFieldValue('prefix');
    const mobileVal = form.getFieldValue('mobile');

    if (isMobileVerified && action !== 'Resend') {
      resetOtp();
      return;
    }
    if (action === 'Resend') setMobileOtp('');

    if (!isMobileLengthValid(mobileVal)) return;

    setApiError(null);
    setOtpError(null);
    setMobileOtpVerified(false);

    const isResend = action === 'Resend';
    if (isResend) setResendLoading(true);
    else setLoading(true);

    try {
      const response = await axios.post(
        resetPassword,
        Encrypt({ action: 'verifyMobile', mobile: mobileForApi(prefix, mobileVal) }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      const token = response.data?.token;

      if (token) {
        setShowCodeInput(true);
        setVerificationToken(token);
        setIsMobileVerified(true);
        form.setFieldsValue({ userName: userNameDisplayFromPrefix(prefix, mobileVal) });
        setResendCountdown(46);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || AUTH.RESET_ERROR_DEFAULT;
      setApiError(msg);
    } finally {
      if (isResend) setResendLoading(false);
      else setLoading(false);
    }
  };

  const confirmMobileOtp = async () => {
    if (!verificationToken || mobileOtp.length < 6) return;
    const prefix = form.getFieldValue('prefix');
    const mobileVal = form.getFieldValue('mobile');
    setApiError(null);
    setConfirmOtpLoading(true);
    try {
      const response = await axios.post(
        resetPassword,
        Encrypt({
          action: 'verifyMobileOTP',
          mobile: mobileForApi(prefix, mobileVal),
          token: verificationToken,
          mobileVerificationCode: mobileOtp,
        }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data?.verifiedMobileToken) {
        setMobileOtpVerified(true);
        dispatch(setResetStep1Data({ prefix, mobile: mobileVal, verifiedMobileToken: response.data.verifiedMobileToken }));
        setStep(2);
      } else {
        setApiError('Verification failed. Please re-verify.');
      }
    } catch (error) {
      const msg = error?.response?.data?.message || AUTH.REGISTER_VERIFY_FAILED;
      setApiError(msg);
    } finally {
      setConfirmOtpLoading(false);
    }
  };

  const handleSubmit = async () => {
    const { password, confirmPassword } = form.getFieldsValue(['password', 'confirmPassword']);
    setSubmitLoading(true);
    setApiError(null);
    setSuccessMsg(null);

    try {
      const response = await axios.post(
        resetPassword,
        Encrypt({
          action: 'updatePassword',
          verifiedMobileToken: resetPasswordFormData.verifiedMobileToken,
          userName: mobileForApi(resetPasswordFormData.prefix, resetPasswordFormData.mobile),
          mobile: mobileForApi(resetPasswordFormData.prefix, resetPasswordFormData.mobile),
          password,
          confirmPassword,
        }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (
        response.status === 200 &&
        response.data?.message?.toLowerCase().includes('success')
      ) {
        setSuccessMsg(response.data.message);
        dispatch(resetResetPasswordData());
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      } else {
        setApiError(response.data?.message || AUTH.RESET_ERROR_UNKNOWN);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || AUTH.RESET_ERROR_FAILED;
      setApiError(msg);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    if (step === 2) {
      form.setFieldsValue({ prefix: resetPasswordFormData.prefix, mobile: resetPasswordFormData.mobile });
      setStep(1);
    } else {
      navigate('/login');
    }
  };

  const canContinue = mobileOtp.length === 6 || mobileOtpVerified;

  return (
    <>
      {apiError && (
        <Alert message={apiError} type="error" showIcon closable className={styles.alert} onClose={() => setApiError(null)} />
      )}
      {successMsg && (
        <Alert message={successMsg} type="success" showIcon className={styles.alert} />
      )}

      <div className={styles.subtitle}>{AUTH.RESET_PAGE_TITLE}</div>

      <div className={styles.steps}>
        {[1, 2].map((n) => (
          <div
            key={n}
            className={`${styles.stepDot} ${n <= step ? styles.stepDotActive : styles.stepDotInactive}`}
          />
        ))}
      </div>

      {step === 1 && (
        <div className={styles.stepLabel}>{AUTH.RESET_STEP1_LABEL}</div>
      )}
      {step === 2 && (
        <div className={styles.stepLabel}>{AUTH.RESET_STEP2_LABEL}</div>
      )}

      {step === 1 && (
        <div className={styles.stepDesc}>{AUTH.RESET_STEP1_DESC}</div>
      )}
      {step === 2 && (
        <div className={styles.stepDesc}>{AUTH.RESET_STEP2_DESC}</div>
      )}

      <Form form={form} layout="vertical" initialValues={{ prefix: '+91' }}>
        {/* ── Step 1: Mobile verify ── */}
        {step === 1 && (
          <>
            <div className={styles.fieldGroup}>
              <div className={styles.fieldLabel}>{AUTH.RESET_MOBILE_LABEL}</div>
              <div className={styles.mobileRow}>
                <div className={styles.countryCode}>
                  <PrefixFormItem disabled={isMobileVerified} />
                </div>
                <Form.Item
                  name="mobile"
                  className={styles.mobileInput}
                  rules={[
                    { required: true, message: AUTH.RESET_MOBILE_REQUIRED },
                    {
                      validator: (_, value) =>
                        !value || isMobileLengthValid(value)
                          ? Promise.resolve()
                          : Promise.reject(new Error(AUTH.REGISTER_MOBILE_INVALID)),
                    },
                  ]}
                >
                  <Input
                    ref={mobileInputRef}
                    disabled={isMobileVerified}
                    maxLength={MOBILE_MAX_LEN}
                    style={mobileFieldInputPlain}
                    placeholder={AUTH.RESET_MOBILE_PLACEHOLDER}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, MOBILE_MAX_LEN);
                      form.setFieldsValue({ mobile: v });
                    }}
                  />
                </Form.Item>
                <Form.Item className={styles.verifyBtn}>
                  <Button
                    size="small"
                    className={`${styles.verifyBtn} ${styles.verifyBtnGhost}`}
                    onClick={() => handleVerifyClick(isMobileVerified ? 'Edit' : '')}
                    disabled={loading || resendLoading || !isMobileLengthValid(mobileWatch)}
                    loading={loading}
                  >
                    {loading ? AUTH.REGISTER_VERIFY_SENDING : isMobileVerified ? AUTH.REGISTER_EDIT : AUTH.REGISTER_VERIFY}
                  </Button>
                </Form.Item>
              </div>

              {showCodeInput && (
                <div className={styles.otpBox}>
                  <div className={styles.otpHeader}>{AUTH.RESET_OTP_HEADER}</div>
                  <OtpInput
                    value={mobileOtp}
                    onChange={(v) => { setMobileOtp(v); setOtpError(null); }}
                    disabled={resendLoading}
                    error={otpError}
                    setError={setOtpError}
                  />
                  {otpError && <div className={styles.otpMsg}>{otpError}</div>}
                  <div className={styles.otpFooter}>
                    <span
                      className={resendCountdown > 0 ? styles.otpActionDisabled : styles.otpAction}
                      onClick={() => resendCountdown <= 0 && !resendLoading && handleVerifyClick('Resend')}
                    >
                      {resendCountdown > 0 ? `Resend in ${fmtCountdown(resendCountdown)}` : AUTH.RESET_OTP_RESEND}
                    </span>
                    <span className={styles.otpSeparator}>·</span>
                    <span className={styles.otpAction} onClick={resetOtp}>{AUTH.RESET_OTP_CHANGE}</span>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.navRow}>
              <Button
                type="primary"
                className={styles.navBtn}
                style={{ flex: 1 }}
                disabled={!canContinue || confirmOtpLoading}
                loading={confirmOtpLoading}
                onClick={() => {
                  if (mobileOtpVerified) {
                    setStep(2);
                  } else {
                    confirmMobileOtp();
                  }
                }}
              >
                {AUTH.RESET_CONTINUE}
              </Button>
            </div>
            <div className={styles.loginLink}>
              {AUTH.RESET_LOGIN_LINK}<span className={styles.linkLike} onClick={() => navigate('/login')}>{AUTH.RESET_LOGIN_CTA}</span>
            </div>
          </>
        )}

        {/* ── Step 2: New password ── */}
        {step === 2 && (
          <>
            <Form.Item
              label={AUTH.RESET_USERNAME_LABEL}
              name="userName"
              rules={[{ required: true, message: AUTH.RESET_USERNAME_REQUIRED }]}
              className={styles.fieldItem}
            >
              <Input disabled />
            </Form.Item>

            <Form.Item
              label={AUTH.RESET_NEW_PASSWORD}
              name="password"
              rules={[
                { required: true, message: AUTH.RESET_PASSWORD_REQUIRED },
                { min: 5, message: AUTH.RESET_PASSWORD_MINLENGTH },
              ]}
              className={styles.passwordField}
            >
              <Input.Password className={styles.passwordInput} />
            </Form.Item>

            <Form.Item
              label={AUTH.RESET_CONFIRM_PASSWORD}
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: AUTH.RESET_CONFIRM_PASSWORD_REQUIRED },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    return !value || getFieldValue('password') === value
                      ? Promise.resolve()
                      : Promise.reject(new Error(AUTH.RESET_PASSWORDS_MISMATCH));
                  },
                }),
              ]}
              className={styles.passwordField}
            >
              <Input.Password className={styles.passwordInput} />
            </Form.Item>

            <div className={styles.navRow}>
              <Button className={styles.navBtn} style={{ flex: 1 }} onClick={handleCancel}>
                {step === 1 ? AUTH.RESET_CANCEL : AUTH.RESET_BACK}
              </Button>
              <Button
                type="primary"
                className={styles.navBtn}
                style={{ flex: 1 }}
                loading={submitLoading}
                onClick={handleSubmit}
              >
                {submitLoading ? AUTH.RESET_SUBMITTING : AUTH.RESET_SUBMIT}
              </Button>
            </div>
          </>
        )}
      </Form>
    </>
  );
}

export default ResetPassword;
